/**
 * Hardest-first question stats (brief §teacher: `/teacher/questions`).
 * Each row shows a plain-text preview of the statement (Markdown/math
 * stripped) even while collapsed; a row expands to show the full statement
 * rendered (Markdown + KaTeX) and, for open questions, each rubric item's
 * miss rate — the share of self-graded attempts that did NOT tick it (never
 * the literal JSON key "rubric", see `service.ts`).
 *
 * `useTeacherQuestions` already returns the whole bank (~400 selectable
 * questions incl. homework, more later) sorted hardest-first by the server.
 * The search box and pager below stay client-side: they filter and slice
 * that same array in place rather than adding server-side query params, so
 * the hardest-first sort is never disturbed.
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  Button,
  EmptyState,
  Input,
  PageHeader,
  ProgressBar,
  Select,
  Spinner,
} from "@client/components/ui";
import { useLang, useT, useTp } from "@client/i18n";
import { formatNumber, formatPercent } from "@client/lib/format";
import { previewStatement } from "@client/lib/preview";
import { Localized, useLocalizedText } from "@client/components/Localized";
import { QuestionParts } from "@client/components/QuestionParts";
import { useTopicsAndChapters } from "@client/api/topics";
import { TeacherNav } from "./TeacherNav";
import { useTeacherQuestions } from "./api";
import type { QuestionStats } from "./types";

const TYPES = ["mc", "numeric", "open"] as const;
const PAGE_SIZE = 25;

/**
 * Case- and diacritics-insensitive matching for the search box: "sok" (or
 * "SOK") still matches "sök", which Swedish question and topic text is full
 * of.
 */
function normalizeForSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function QuestionRow({
  q,
  expanded,
  onToggle,
}: {
  q: QuestionStats;
  expanded: boolean;
  onToggle: () => void;
}) {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const localize = useLocalizedText();
  const metric = q.autoAccuracy ?? q.selfScoreRatio;
  // Math and Markdown stripped for the collapsed row — the full statement
  // (with $…$ math and Markdown syntax intact) only renders once expanded.
  const preview = previewStatement(localize(q.statement));

  return (
    <li className="border-b border-ink-100 py-3 last:border-0 dark:border-ink-900">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-start gap-3 text-left"
      >
        {expanded ? (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink-800 dark:text-ink-100">
            {q.questionId}
          </span>
          <span className="text-xs text-ink-500 dark:text-ink-400">
            {t("teacher.chapterLabel")} {q.chapter} · {t(`teacher.questionType.${q.type}`)} ·{" "}
            {tp("teacher.questions.attempts", q.attempts)}
          </span>
          {/* Hidden once expanded — the full statement below already carries
              this text, rendered properly instead of as plain preview text. */}
          {!expanded && (
            <span className="mt-0.5 block truncate text-xs text-ink-500 dark:text-ink-400">
              {preview}
            </span>
          )}
        </span>
        <span className="w-24 shrink-0 text-right text-sm tabular-nums text-ink-700 dark:text-ink-200">
          {metric === null ? t("teacher.questions.noAttempts") : formatPercent(metric, lang)}
        </span>
      </button>
      {expanded && (
        <div className="mt-3 ml-7 space-y-3">
          <Localized value={q.statement} className="text-sm text-ink-700 dark:text-ink-200" />
          <QuestionParts parts={q.parts} className="mt-0 text-sm" />
          {q.type === "open" && q.rubricMissRates.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">
                {t("teacher.questions.rubricMissRates.title")}
              </p>
              <ul className="space-y-2">
                {q.rubricMissRates.map((r) => (
                  <li key={r.rubricId} className="flex items-center gap-3">
                    <span className="min-w-0 flex-1 text-sm text-ink-700 dark:text-ink-200">
                      <Localized value={r.text} variant="inline" />
                    </span>
                    <ProgressBar value={r.missRate} tone="warning" className="w-24" />
                    <span className="w-12 shrink-0 text-right text-xs tabular-nums text-ink-600 dark:text-ink-400">
                      {formatPercent(r.missRate, lang)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function TeacherQuestionsPage() {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const localize = useLocalizedText();
  const [chapter, setChapter] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);
  const query = useTeacherQuestions(chapter ? Number(chapter) : undefined, type || undefined);
  const topicsQuery = useTopicsAndChapters();
  // The chapter filter comes from the taxonomy, not a hardcoded 1…8 — chapter 9
  // ("Statistisk mekanik") must be filterable the moment it has questions.
  const chapters = (topicsQuery.data?.chapters ?? []).map((c) => c.number);

  const topicNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const topic of topicsQuery.data?.topics ?? []) {
      map.set(topic.id, topic.name[lang]);
    }
    return map;
  }, [topicsQuery.data, lang]);

  const normalizedSearch = normalizeForSearch(search.trim());
  const filtered = useMemo(() => {
    const rows = query.data ?? [];
    if (!normalizedSearch) return rows;
    return rows.filter((q) => {
      const topicName = topicNameById.get(q.topic) ?? q.topic;
      const haystack = normalizeForSearch(`${q.questionId} ${topicName} ${localize(q.statement)}`);
      return haystack.includes(normalizedSearch);
    });
  }, [query.data, normalizedSearch, topicNameById, localize]);

  // A stale page number would otherwise show an empty page (or the wrong
  // rows) right after a search or filter narrows the list.
  useEffect(() => {
    setPage(1);
  }, [chapter, type, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title={t("teacher.questions.title")}
        description={t("teacher.questions.description")}
      />
      <TeacherNav />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Input
          label={t("teacher.questions.search.label")}
          placeholder={t("teacher.questions.search.placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          containerClassName="min-w-[200px] flex-1"
        />
        <Select
          label={t("teacher.questions.filter.chapter")}
          value={chapter}
          onChange={(e) => setChapter(e.target.value)}
          options={[
            { value: "", label: t("teacher.questions.filter.allChapters") },
            ...chapters.map((c) => ({ value: String(c), label: String(c) })),
          ]}
        />
        <Select
          label={t("teacher.questions.filter.type")}
          value={type}
          onChange={(e) => setType(e.target.value)}
          options={[
            { value: "", label: t("teacher.questions.filter.allTypes") },
            ...TYPES.map((ty) => ({ value: ty, label: t(`teacher.questionType.${ty}`) })),
          ]}
        />
      </div>

      {query.isLoading && <Spinner size="md" label={t("common.loading")} />}
      {!query.isLoading && !query.data && <EmptyState title={t("common.error")} />}

      {query.data && filtered.length === 0 && <EmptyState title={t("teacher.questions.empty")} />}

      {query.data && filtered.length > 0 && (
        <>
          <ul>
            {pageRows.map((q) => (
              <QuestionRow
                key={q.questionId}
                q={q}
                expanded={expanded === q.questionId}
                onToggle={() =>
                  setExpanded((current) => (current === q.questionId ? null : q.questionId))
                }
              />
            ))}
          </ul>
          <div className="mt-4 flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("teacher.questions.pagination.previous")}
            </Button>
            <span className="text-xs text-ink-500 dark:text-ink-400">
              {t("teacher.questions.pagination.status", {
                page: formatNumber(currentPage, lang),
                total: formatNumber(totalPages, lang),
              })}
            </span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t("common.next")}
            </Button>
          </div>
        </>
      )}
      {query.data && (
        <p className="mt-4 text-xs text-ink-500 dark:text-ink-400">
          {tp("teacher.questions.count", filtered.length, {
            count: formatNumber(filtered.length, lang),
          })}
        </p>
      )}
    </div>
  );
}

export default TeacherQuestionsPage;
